import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import Icon from 'carbon-react/lib/components/icon';

import { UIDragListItem, UIDragListItemTypes } from '@futrli/ui-components';
import { useDropzone } from '@futrli/shared-services';

import { OffsetEliminationGroup } from '../../../../../core/api/consolidation';
import { useConsolidationContext } from '../../../../consolidation-context/consolidation-context';
import styles from './offset-eliminated-group.scss';

export interface OffsetEliminatedGroupProps {
  allowDrop?: boolean;
  categoryLabel: string;
  elimination: OffsetEliminationGroup;
  isDragging?: boolean;
  onDragEnd?: (event: React.DragEvent) => void;
  onDragStart?: (event: React.DragEvent, id: string) => void;
  onDrop?: (event: React.DragEvent, group: OffsetEliminationGroup) => void;
}

export const OffsetEliminatedGroup = ({
  allowDrop = false,
  categoryLabel,
  elimination,
  isDragging,
  onDragEnd,
  onDragStart,
  onDrop
}: OffsetEliminatedGroupProps) => {
  const {
    eliminations: {
      coa: { index },
      handleEditEliminationGroup,
      handleRemoveEliminationGroup,
      handleRemoveEliminationGroupAccount
    }
  } = useConsolidationContext();
  const intl = useIntl();

  const { handleDragLeave, handleDragOver, targetted } = useDropzone(allowDrop, !!isDragging);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      onDrop?.(event, elimination);
    },
    [elimination, onDrop]
  );

  return (
    <div
      className={clsx(styles.eliminationGroup, {
        [styles.targetable]: isDragging && allowDrop,
        [styles.targetted]: targetted
      })}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={styles.groupLabel}>
        {elimination.name}
        <button
          onClick={() => handleEditEliminationGroup(elimination)}
          className={clsx(styles.iconButton, styles.edit)}
          aria-label={intl.formatMessage({
            id: 'consolidation.edit_elimination',
            defaultMessage: 'Edit elimination group'
          })}
        >
          <Icon type="edit" fontSize="small" color="#ccd6db" />
        </button>
        <button
          onClick={() => handleRemoveEliminationGroup(elimination)}
          className={clsx(styles.iconButton, styles.trash)}
          aria-label={intl.formatMessage({
            id: 'consolidation.delete_elimination',
            defaultMessage: 'Delete elimination group'
          })}
        >
          <Icon type="delete" fontSize="small" color="#ccd6db" />
        </button>
        <span className={styles.inLabel}>
          {intl.formatMessage(
            {
              id: 'consolidation.in_category',
              defaultMessage: 'In {category}'
            },
            { category: categoryLabel }
          )}
        </span>
      </div>
      <ul className={clsx(styles.accountsList, { [styles.empty]: !elimination.accounts.length })}>
        {elimination.accounts.map(accountId => (
          <li className={styles.account} key={accountId}>
            <UIDragListItem
              id={accountId}
              type={UIDragListItemTypes.REMOVE}
              onClick={() => handleRemoveEliminationGroupAccount(accountId)}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              <div className={styles.name}>{index?.[accountId]?.label || '???'}</div>
            </UIDragListItem>
          </li>
        ))}
      </ul>
    </div>
  );
};
